import type { SalesforceRecord } from '../../types/salesforce';

const SF_API_VERSION = 'v59.0';

export async function apiRequest(
  endpoint: string,
  options: RequestInit = {},
  signal?: AbortSignal
): Promise<Response> {
  const proxyUrl = `/api/salesforce${endpoint}`;

  return fetch(proxyUrl, {
    ...options,
    signal,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
}

export async function executeQuery<T = SalesforceRecord>(
  soql: string,
  signal?: AbortSignal
): Promise<T[]> {
  const response = await apiRequest(
    `/services/data/${SF_API_VERSION}/query?q=${encodeURIComponent(soql)}`,
    {},
    signal
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`SOQL query failed: ${errorText}`);
  }

  let data = await response.json();
  let allRecords: T[] = data.records || [];

  while (data.nextRecordsUrl) {
    const nextResponse = await apiRequest(data.nextRecordsUrl, {}, signal);
    if (!nextResponse.ok) {
      throw new Error('SOQL pagination failed');
    }
    data = await nextResponse.json();
    allRecords = allRecords.concat(data.records || []);
  }

  return allRecords;
}

/**
 * Run an aggregate `SELECT COUNT() ...` query and return the row count.
 * COUNT() responses carry the number in `totalSize` with an empty `records`
 * array, so `executeQuery` (which returns records) can't surface it.
 */
export async function countQuery(
  soql: string,
  signal?: AbortSignal
): Promise<number> {
  const response = await apiRequest(
    `/services/data/${SF_API_VERSION}/query?q=${encodeURIComponent(soql)}`,
    {},
    signal
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`SOQL count query failed: ${errorText}`);
  }

  const data = await response.json();
  return data.totalSize ?? 0;
}

/**
 * Run a `SELECT COUNT_DISTINCT(field) total FROM …` query and return the count.
 * COUNT_DISTINCT is an aggregate (not reflected in `totalSize`, which `countQuery`
 * reads), so the value comes back on the first record — aliased `total`, or `expr0`
 * if no alias was given.
 */
export async function countDistinctQuery(
  soql: string,
  signal?: AbortSignal
): Promise<number> {
  const response = await apiRequest(
    `/services/data/${SF_API_VERSION}/query?q=${encodeURIComponent(soql)}`,
    {},
    signal
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`SOQL count-distinct query failed: ${errorText}`);
  }

  const data = await response.json();
  const record = (data.records && data.records[0]) || {};
  return Number(record.total ?? record.expr0 ?? 0);
}

/**
 * Update fields on a single Salesforce record (PATCH sObject row).
 * Salesforce returns 204 No Content on success.
 */
export async function updateRecord(
  objectName: string,
  id: string,
  fields: Record<string, unknown>,
  signal?: AbortSignal
): Promise<void> {
  const response = await apiRequest(
    `/services/data/${SF_API_VERSION}/sobjects/${objectName}/${id}`,
    {
      method: 'PATCH',
      body: JSON.stringify(fields),
    },
    signal
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to update ${objectName} ${id}: ${errorText}`);
  }
}
